import React, { useState } from 'react';
import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Checkbox
} from '@mui/material';
import Swal from 'sweetalert2';
import { SapService } from '../../services/api';
import { fechaLocalHoy } from '../../constantes/constants';

export default function MaterialesCreados() {
    const [fecha, setFecha] = useState(fechaLocalHoy());
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    // Email Modal State
    const [openEmailModal, setOpenEmailModal] = useState(false);
    const [emailTo, setEmailTo] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);

    const handleConsultar = async () => {
        if (!fecha) return;
        setLoading(true);
        setSelectedIds([]); // Reset selection on new query
        try {
            const result = await SapService.getMaterialesPorFecha(fecha);
            if (Array.isArray(result)) {
                setMaterials(result);
                if (result.length === 0) {
                    Swal.fire({ icon: 'info', title: 'Sin resultados', text: 'No se encontraron materiales para esta fecha.' });
                }
            } else {
                setMaterials([]);
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: error.message });
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = materials.map((n) => n.idregistros || n.id);
            setSelectedIds(newSelecteds);
            return;
        }
        setSelectedIds([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selectedIds.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedIds, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedIds.slice(1));
        } else if (selectedIndex === selectedIds.length - 1) {
            newSelected = newSelected.concat(selectedIds.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedIds.slice(0, selectedIndex),
                selectedIds.slice(selectedIndex + 1),
            );
        }
        setSelectedIds(newSelected);
    };

    const handleOpenEmailModal = () => {
        if (selectedIds.length === 0) {
            Swal.fire({ icon: 'warning', title: 'Atención', text: 'Debe seleccionar al menos un material para enviar.' });
            return;
        }
        setOpenEmailModal(true);
    };

    const handleCloseEmailModal = () => {
        setOpenEmailModal(false);
        setEmailTo('');
    };

    const generateHtmlTable = (data) => {
        console.log("Data for email:", data);
        let rows = data.map(m => {
            return `
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">${m.error || 'N/A'}</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${m.nombreItem || ''}</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${m.categoriaProducto || ''}</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${m.proveedor || ''}</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${m.estado || ''}</td>
            </tr>
        `}).join('');

        return `
            <h2>Reporte de Materiales Creados - Fecha: ${fecha}</h2>
            <p>Se adjuntan los ${data.length} materiales seleccionados.</p>
            <table style="border-collapse: collapse; width: 100%;">
                <thead>
                    <tr style="background-color: #f2f2f2;">
                        <th style="border: 1px solid #ddd; padding: 8px;">Código</th>
                        <th style="border: 1px solid #ddd; padding: 8px;">Nombre</th>
                        <th style="border: 1px solid #ddd; padding: 8px;">Categoría</th>
                        <th style="border: 1px solid #ddd; padding: 8px;">Proveedor</th>
                        <th style="border: 1px solid #ddd; padding: 8px;">Estado</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows}
                </tbody>
            </table>
        `;
    };

    const handleSendEmail = async () => {
        if (!emailTo) {
            Swal.fire({ icon: 'warning', text: 'Por favor ingrese un correo electrónico.' });
            return;
        }

        const selectedMaterials = materials.filter(m => selectedIds.includes(m.idregistros || m.id));

        if (selectedMaterials.length === 0) {
            Swal.fire({ icon: 'warning', text: 'No hay materiales seleccionados.' });
            return;
        }

        setSendingEmail(true);
        try {
            const htmlContent = generateHtmlTable(selectedMaterials);
            const emailData = {
                to: emailTo,
                subject: `Reporte de Materiales Creados (${selectedMaterials.length}) - ${fecha}`,
                text: `Se adjunta el reporte de ${selectedMaterials.length} materiales seleccionados del día ${fecha}.`,
                html: htmlContent
            };

            await SapService.enviarCorreos(emailData);

            Swal.fire({ icon: 'success', title: 'Enviado', text: 'El correo ha sido enviado exitosamente.' });
            handleCloseEmailModal();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo enviar el correo.' });
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 5, mb: 10 }}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, background: '#fff', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Materiales Creados
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, my: 3, alignItems: 'center' }}>
                    <TextField
                        label="Fecha"
                        type="date"
                        value={fecha}
                        onChange={(e) => setFecha(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 220 }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleConsultar}
                        disabled={loading}
                        sx={{ height: 56, px: 4 }}
                    >
                        {loading ? 'Consultando...' : 'Consultar'}
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleOpenEmailModal}
                        disabled={selectedIds.length === 0}
                        sx={{ height: 56 }}
                    >
                        Enviar Seleccionados ({selectedIds.length})
                    </Button>
                </Box>

                <TableContainer component={Paper} elevation={0} variant="outlined">
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        color="primary"
                                        indeterminate={selectedIds.length > 0 && selectedIds.length < materials.length}
                                        checked={materials.length > 0 && selectedIds.length === materials.length}
                                        onChange={handleSelectAll}
                                        inputProps={{
                                            'aria-label': 'select all desserts',
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Nombre Item</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Categoría</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Proveedor</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {materials.length > 0 ? (
                                materials.map((row) => {
                                    const isItemSelected = selectedIds.indexOf(row.idregistros || row.id) !== -1;
                                    const labelId = `enhanced-table-checkbox-${row.idregistros || row.id}`;

                                    return (
                                        <TableRow
                                            hover
                                            onClick={(event) => handleClick(event, row.idregistros || row.id)}
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.idregistros || row.id}
                                            selected={isItemSelected}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={isItemSelected}
                                                    inputProps={{
                                                        'aria-labelledby': labelId,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{row.codigoMaterial}</TableCell>
                                            <TableCell>{row.nombreItem}</TableCell>
                                            <TableCell>{row.categoriaProducto}</TableCell>
                                            <TableCell>{row.proveedor?.split('|')[0] || row.proveedor}</TableCell>
                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        px: 1, py: 0.5, borderRadius: 1, display: 'inline-block',
                                                        bgcolor: row.estado === 'Completado' ? '#dcfce7' : '#fee2e2',
                                                        color: row.estado === 'Completado' ? '#166534' : '#991b1b',
                                                        fontWeight: 'medium', fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {row.estado}
                                                </Box>
                                            </TableCell>
                                            {/* Hasta 3 líneas: el motivo del rechazo va al inicio del texto. Completo en el tooltip */}
                                            <TableCell sx={{ maxWidth: 320 }} title={row.error}>
                                                <Box sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
                                                    {row.error}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        No hay datos para mostrar
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Email Modal */}
            <Dialog open={openEmailModal} onClose={handleCloseEmailModal}>
                <DialogTitle>Enviar Reporte por Correo</DialogTitle>
                <DialogContent sx={{ minWidth: 400, mt: 1 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Correo Electrónico"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={emailTo}
                        onChange={(e) => setEmailTo(e.target.value)}
                        placeholder="ejemplo@talsa.com.co"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEmailModal} disabled={sendingEmail}>Cancelar</Button>
                    <Button onClick={handleSendEmail} variant="contained" disabled={sendingEmail}>
                        {sendingEmail ? 'Enviando...' : 'Enviar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
